// Must match device-management-library models/softwareImport RequiredColumns.
export const REQUIRED_COLUMNS = [
  'Name',
  'Site Name',
  'Application Name',
  'Version',
  'Publisher',
];

export const OPTIONAL_COLUMNS = [
  'Friendly Name',
  'Device Type',
  'OS',
  'IP Address',
  'Last Logged On User',
  'Install Date',
  'Date Checked',
  'Device Time Zone',
];

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

const HEADER_PREVIEW_BYTES = 64 * 1024;

export const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let idx = 0; idx < line.length; idx++) {
    const char = line[idx];
    if (inQuotes) {
      if (char === '"' && line[idx + 1] === '"') {
        current += '"';

        idx++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);

      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);

  return fields.map((field) => field.trim());
};

// Checks the file type, size and header row before uploading so format problems are reported immediately.
// Returns an error message, or null when the file looks importable.
export const validateCsvFile = async (file: File): Promise<string | null> => {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    if (/\.xlsx?$/i.test(file.name)) {
      return 'This is an Excel workbook. Save it as CSV (Comma delimited) and upload the .csv file.';
    }
    return 'Only .csv files can be imported.';
  }
  if (file.size === 0) {
    return 'The file is empty.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'The file is larger than 50 MB. Split it into smaller files.';
  }

  const preview = await file.slice(0, HEADER_PREVIEW_BYTES).text();
  const headerLine = preview.replace(/^\uFEFF/, '').split(/\r?\n/)[0] ?? '';
  const columns = parseCsvLine(headerLine).map((column) =>
    column.toLowerCase(),
  );

  const missing = REQUIRED_COLUMNS.filter(
    (column) => !columns.includes(column.toLowerCase()),
  );
  if (missing.length > 0) {
    return `The file is missing required column(s): ${missing.join(', ')}.`;
  }

  const lines = preview.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length < 2) {
    return 'The file has a header row but no data rows.';
  }

  return null;
};

export const readErrorMessage = async (response: Response) => {
  const text = await response.text();
  try {
    const body = JSON.parse(text);
    return body.developerMessage || body.message || response.statusText;
  } catch {
    return text || response.statusText;
  }
};
